import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Eye, EyeOff, Loader2, Lock, Mail, ArrowLeft, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function AmbientDots({ isDark }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    const dots = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    for (let i = 0; i < 40; i++) {
      dots.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
        r: Math.random() * 1.6 + 0.4, alpha: Math.random() * 0.1 + 0.02,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(59, 130, 246, ${p.alpha})`
          : `rgba(37, 99, 235, ${p.alpha * 0.5})`;
        ctx.fill();
      });
      animFrame = requestAnimationFrame(draw);
    };
    animFrame = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animFrame); window.removeEventListener('resize', resize); };
  }, [isDark]);
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotTouched, setForgotTouched] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('remember_me') === 'true');

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const from = location.state?.from?.pathname || '/dashboard';
  const googleBtnRef = useRef(null);
  const googleCodeClientRef = useRef(null);

  // Redirect already-authenticated users away from login page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Resolve the Google client_id from the BACKEND at runtime so the frontend can
    // never drift from the client_id/secret pair the backend exchanges codes with
    // (this is what caused the production `unauthorized_client` error). Fall back to
    // the build-time env only if the backend call fails.
    const initGoogle = async () => {
      let clientId = GOOGLE_CLIENT_ID;
      try {
        const res = await authAPI.getGoogleClientId();
        const backendId = res?.data?.client_id || res?.data?.data?.client_id;
        if (backendId) clientId = backendId;
      } catch (e) { /* keep build-time fallback */ }
      if (cancelled) return;
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (!clientId) { setGoogleUnavailable(true); return; }
        if (window.google?.accounts?.oauth2) {
          // Auth-code flow (popup) — the ONLY GIS flow that lets us pass
          // `prompt=select_account`, which forces Google's account chooser
          // instead of silently pre-selecting the browser's active session.
          try {
            googleCodeClientRef.current = window.google.accounts.oauth2.initCodeClient({
              client_id: clientId,
              scope: 'openid email profile',
              ux_mode: 'popup',
              prompt: 'select_account',
              callback: handleGoogleCodeResponse,
            });
          } catch (e) {
            setGoogleUnavailable(true);
          }
        } else {
          setGoogleUnavailable(true);
        }
      };
      script.onerror = () => setGoogleUnavailable(true);
      document.body.appendChild(script);
    };
    initGoogle();
    return () => { cancelled = true; const s = document.querySelector('script[src="https://accounts.google.com/gsi/client"]'); if (s) s.remove(); };
  }, []);

  const handleGoogleCodeResponse = async (response) => {
    if (!response?.code) {
      if (response?.error && response.error !== 'popup_closed') {
        toast.error('Google sign-in failed', { description: response.error });
      }
      return;
    }
    setGoogleLoading(true);
    try {
      const result = await authAPI.googleCode({ code: response.code });
      if (result.data?.success) {
        localStorage.setItem('access_token', result.data.access_token);
        localStorage.setItem('refresh_token', result.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(result.data.user));
        toast.success(result.data.is_new_user ? 'Welcome! Account created.' : 'Welcome back!');
        window.location.href = result.data.is_new_user ? '/onboarding/brain-init' : '/dashboard';
      }
    } catch (e) {
      toast.error('Google sign-in failed', { description: e?.response?.data?.detail || 'Please try again.' });
    }
    finally { setGoogleLoading(false); }
  };

  const handleGoogleSignIn = () => {
    if (googleCodeClientRef.current) {
      try {
        googleCodeClientRef.current.requestCode();
      } catch (e) {
        toast.error('Google sign-in failed to open. Please try again.');
      }
    } else {
      toast.error('Google Sign-In is loading. Please try again.');
    }
  };

  useEffect(() => {
    const expired = searchParams.get('expired') === 'true' || localStorage.getItem('session_expired') === 'true';
    if (expired) { setSessionExpired(true); localStorage.removeItem('session_expired'); }
  }, [searchParams]);

  useEffect(() => {
    if (touched.email) {
      if (!email) setEmailError('Email address is required');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) setEmailError('Please enter a valid email');
      else setEmailError('');
    }
  }, [email, touched.email]);

  useEffect(() => {
    if (touched.password) {
      if (!password) setPasswordError('Password is required');
      else if (password.length < 6) setPasswordError('Password must be at least 6 characters');
      else setPasswordError('');
    }
  }, [password, touched.password]);

  useEffect(() => {
    if (forgotTouched) {
      if (!forgotEmail) setForgotEmailError('Email address is required');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) setForgotEmailError('Please enter a valid email');
      else setForgotEmailError('');
    }
  }, [forgotEmail, forgotTouched]);

  const validateForm = () => {
    let ok = true;
    if (!email) { setEmailError('Email address is required'); ok = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('Please enter a valid email'); ok = false; }
    if (!password) { setPasswordError('Password is required'); ok = false; }
    else if (password.length < 6) { setPasswordError('Password must be at least 6 characters'); ok = false; }
    return ok;
  };

  const getErrorMessage = (err) => {
    // Timeout / network abort (e.g. backend cold-start) — NOT a credentials problem.
    if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || !err?.response) {
      return 'The server is waking up. Please wait a moment and click Sign in again.';
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') {
      if (detail.toLowerCase().includes('invalid') || detail.toLowerCase().includes('incorrect')) return 'The email or password you entered is incorrect.';
      if (detail.toLowerCase().includes('not found') || detail.toLowerCase().includes('no user')) return 'No account found with this email. Please sign up.';
      if (detail.toLowerCase().includes('locked') || detail.toLowerCase().includes('disabled')) return 'Account temporarily locked. Please try again later.';
      return detail;
    }
    return 'Unable to sign in. Please check your credentials.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setTouched({ email: true, password: true });
    if (!validateForm()) return;
    setLoading(true);
    try {
      const userData = await login(email, password, rememberMe);
      toast.success('Welcome back!');
      localStorage.setItem('lastActivePage', from);
      const isAdmin = !userData.module_permissions;
      const hasAdminRole = userData.department_permissions && Object.values(userData.department_permissions).some(d => d.role === 'admin');
      if (from && from !== '/login' && from !== '/signup') navigate(from, { replace: true });
      else if (isAdmin || hasAdminRole) navigate('/dashboard', { replace: true });
      else navigate('/user-dashboard', { replace: true });
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      const isWarmup = err?.code === 'ECONNABORTED' || !err?.response;
      toast.error(isWarmup ? 'Server is waking up — please try again' : 'Login failed');
    }
    finally { setLoading(false); }
  };

  const handleForgotPasswordClick = () => {
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) setForgotEmail(email);
    setShowForgotPassword(true); setForgotSuccess(false); setForgotEmailError(''); setForgotTouched(false);
  };
  const handleBackToLogin = () => { setShowForgotPassword(false); setForgotSuccess(false); setForgotEmail(''); setForgotEmailError(''); setForgotTouched(false); };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotTouched(true);
    if (!forgotEmail) { setForgotEmailError('Email address is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) { setForgotEmailError('Please enter a valid email'); return; }
    setForgotLoading(true); setForgotEmailError('');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = function () {
      setForgotLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) { setForgotSuccess(true); toast.success('If your account exists, a reset link is on its way.'); }
        else setForgotEmailError(data.detail || `Request failed (${xhr.status})`);
      } catch { setForgotEmailError('Unable to process response'); }
    };
    xhr.onerror = function () { setForgotLoading(false); setForgotEmailError('Unable to connect. Please try again.'); };
    xhr.send(JSON.stringify({ email: forgotEmail }));
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 py-12 relative transition-colors duration-500 ${isDark ? 'bg-background' : 'bg-[hsl(220,14%,97%)]'}`} data-testid="login-page">
      <AmbientDots isDark={isDark} />

      <style>{`
        @keyframes authFadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes authFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes authLogoIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Top bar */}
      <div className="fixed top-6 left-8 z-50 animate-[authLogoIn_0.5s_ease-out]">
        <div className={`w-11 h-11 rounded-full overflow-hidden ring-1 ${isDark ? 'ring-white/10' : 'ring-black/10'} shadow-lg`} data-testid="lockene-logo">
          <img src="https://customer-assets.emergentagent.com/job_91eeaace-707a-4157-ada5-289529392665/artifacts/z8mxrkdr_Logosdcvdcvd%20%282%29.png" alt="Lockene AI" className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="fixed top-6 right-8 z-50 animate-[authFadeIn_0.5s_ease-out_0.2s_both]"><ThemeToggle /></div>

      {/* Card */}
      <div className={`w-full max-w-md rounded-2xl border backdrop-blur-xl relative z-10 animate-[authFadeUp_0.5s_ease-out_0.1s_both] ${isDark ? 'bg-card/80 border-border shadow-2xl shadow-black/30' : 'bg-white/90 border-border shadow-xl shadow-gray-200/60'}`} data-testid="login-card">
        {!showForgotPassword ? (
          <>
            <div className="pt-10 px-8 pb-2">
              <h1 className="text-2xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="login-heading">Welcome Back</h1>
              <p className="text-sm mt-1 text-muted-foreground">Sign in to your Lockene account</p>
            </div>
            <div className="px-8 pb-10 pt-4">
              {googleLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">Signing you in with Google...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {sessionExpired && (
                    <Alert className="border-amber-500/30 bg-amber-500/10" data-testid="session-expired-message">
                      <Clock className="h-4 w-4 text-amber-400" />
                      <AlertDescription className="text-amber-400 text-sm">Session expired. Please log in again.</AlertDescription>
                    </Alert>
                  )}
                  {error && (
                    <Alert className="border-destructive/30 bg-destructive/10" data-testid="login-error-message">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      <AlertDescription className="text-destructive text-sm">{error}</AlertDescription>
                    </Alert>
                  )}

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleUnavailable}
                    data-testid="login-google"
                    className={`w-full flex items-center justify-center gap-3 h-11 rounded-xl border font-medium text-sm transition
                      ${isDark
                        ? 'bg-white text-gray-900 border-white/10 hover:bg-gray-100'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.12-.84 2.07-1.79 2.71v2.25h2.9c1.7-1.56 2.69-3.87 2.69-6.6z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.25c-.8.54-1.83.86-3.06.86-2.35 0-4.35-1.59-5.06-3.72H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.94 10.71A5.4 5.4 0 0 1 3.65 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.98-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.98 2.33C4.65 5.17 6.65 3.58 9 3.58z"/></svg>
                    Continue with Google
                  </button>
                  {googleUnavailable && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-1" data-testid="login-google-unavailable">
                      Google sign-in is unavailable on this domain — this site's origin may not be authorized for the Google client. Use email sign-in below.
                    </p>
                  )}

                  <div className="relative">
                    <div className="h-px bg-border" />
                    <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs text-muted-foreground ${isDark ? 'bg-card' : 'bg-white'}`}>or continue with email</span>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className={`text-sm font-medium ${emailError ? 'text-destructive' : 'text-foreground/70'}`}>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(p => ({ ...p, email: true }))} className={`pl-10 h-11 rounded-xl ${emailError ? 'border-destructive/50 focus:border-destructive/50' : ''}`} disabled={loading} data-testid="login-email-input" />
                    </div>
                    {emailError && <p className="text-xs text-destructive flex items-center gap-1" data-testid="email-error"><AlertCircle className="h-3 w-3" />{emailError}</p>}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className={`text-sm font-medium ${passwordError ? 'text-destructive' : 'text-foreground/70'}`}>Password</Label>
                      <button type="button" onClick={handleForgotPasswordClick} className="text-xs text-primary hover:text-primary/80 hover:underline focus:outline-none" data-testid="forgot-password-link">Forgot Password?</button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => setTouched(p => ({ ...p, password: true }))} className={`pl-10 pr-10 h-11 rounded-xl ${passwordError ? 'border-destructive/50 focus:border-destructive/50' : ''}`} disabled={loading} data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/60 transition-colors focus:outline-none" tabIndex={-1} data-testid="toggle-password-visibility">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordError && <p className="text-xs text-destructive flex items-center gap-1" data-testid="password-error"><AlertCircle className="h-3 w-3" />{passwordError}</p>}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={rememberMe}
                      onClick={() => setRememberMe(!rememberMe)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${rememberMe ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      disabled={loading}
                      data-testid="remember-me-toggle"
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${rememberMe ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <label
                      className="text-sm text-muted-foreground cursor-pointer select-none"
                      onClick={() => !loading && setRememberMe(!rememberMe)}
                      data-testid="remember-me-label"
                    >
                      Remember me for 30 days
                    </label>
                  </div>

                  <Button type="submit" className="w-full h-11 mt-1 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/20 transition-all duration-200" disabled={loading} data-testid="login-form-submit-button">
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : 'Sign In'}
                  </Button>

                  <p className="text-center text-sm pt-2 text-muted-foreground">
                    Don't have an account?{' '}
                    <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">Sign up</Link>
                  </p>
                </form>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="pt-10 px-8 pb-2">
              <div className="flex items-center gap-3">
                <button type="button" onClick={handleBackToLogin} className="p-1.5 rounded-lg transition-colors focus:outline-none hover:bg-muted text-muted-foreground" data-testid="back-to-login-button">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h1 className="text-2xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Reset Password</h1>
              </div>
              <p className="text-sm mt-2 text-muted-foreground">{forgotSuccess ? 'Check your email for reset instructions' : 'Enter your email for password reset'}</p>
            </div>
            <div className="px-8 pb-10 pt-4">
              {forgotSuccess ? (
                <div className="space-y-5" data-testid="forgot-password-success">
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 bg-emerald-500/10 border border-emerald-500/20">
                      <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-foreground">Check your email</h3>
                    <p className="text-sm max-w-xs text-muted-foreground">If an account exists for <strong className="text-foreground">{forgotEmail}</strong>, we've sent a password reset link. Check your inbox &amp; spam folder.</p>
                  </div>
                  <Alert className="bg-blue-500/10 border-blue-500/20">
                    <AlertCircle className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-400 text-sm">The link expires in 60 minutes and can be used once. You'll choose a new password.</AlertDescription>
                  </Alert>
                  <Button type="button" className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" onClick={handleBackToLogin} data-testid="return-to-login-button">Return to Login</Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPasswordSubmit} className="space-y-5" data-testid="forgot-password-form">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className={`text-sm font-medium ${forgotEmailError ? 'text-destructive' : 'text-foreground/70'}`}>Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="forgot-email" type="email" placeholder="your@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} onBlur={() => setForgotTouched(true)} className={`pl-10 h-11 rounded-xl ${forgotEmailError ? 'border-destructive/50' : ''}`} disabled={forgotLoading} autoFocus data-testid="forgot-email-input" />
                    </div>
                    {forgotEmailError && <p className="text-xs text-destructive flex items-center gap-1" data-testid="forgot-email-error"><AlertCircle className="h-3 w-3" />{forgotEmailError}</p>}
                  </div>
                  <Button type="submit" className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" disabled={forgotLoading} data-testid="send-reset-email-button">
                    {forgotLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send Reset Instructions'}
                  </Button>
                  <p className="text-center text-sm pt-2 text-muted-foreground">
                    Remember your password?{' '}
                    <button type="button" onClick={handleBackToLogin} className="text-primary font-medium hover:underline focus:outline-none" data-testid="back-to-login-link">Back to Login</button>
                  </p>
                </form>
              )}
            </div>
          </>
        )}

        <p className="px-8 pb-6 text-center text-xs text-muted-foreground/70" data-testid="auth-legal-links">
          By continuing you agree to our{' '}
          <a href="https://lockene.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="terms-link">Terms &amp; Conditions</a>
          {' '}and{' '}
          <a href="https://lockene.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="privacy-link">Privacy Policy</a>.
        </p>
      </div>

      <p className="fixed bottom-4 left-0 right-0 text-center text-xs z-10 animate-[authFadeIn_0.5s_ease-out_0.8s_both] text-muted-foreground/50" data-testid="login-footer">
        Design and Developed by Lockene Inc
      </p>
    </div>
  );
}
